import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {axiosInstance} from '../routes/axiosInstance';
import moment from "moment";
import {appendLatestSessionOnlyParam} from './forecastQueryUtils';

export const uploadFile = createAsyncThunk(
  'data/uploadFile',
  async ({file, description, resourceId, editingFile}, {rejectWithValue}) => {
    try {
      const formData = new FormData();
      formData.append('description', description);
      if (file) formData.append('file', file);
      if (resourceId !== undefined) {
        formData.append('resource', resourceId === null ? '' : resourceId);
      }

      const url = editingFile ? `/data/file-uploads/${editingFile.id}/` : '/data/file-uploads/';
      const method = editingFile ? 'PATCH' : 'POST';

      const response = await axiosInstance({
        url,
        method,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to upload file.');
    }
  }
);
export const deleteFile = createAsyncThunk(
  'data/deleteFile',
  async ({ fileId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/data/file-uploads/${fileId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to delete file.');
    }
  });

export const downloadFile = createAsyncThunk(
  'data/downloadFile',
  async ({ fileUrl, filename }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(fileUrl, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      return rejectWithValue('Failed to download file.');
    }
  }
);

export const fetchGenericFiles = createAsyncThunk(
  'data/fetchGenericFiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/data/file-uploads/`);
      const data = response.data;
      return data.results;
    } catch (error) {
      return rejectWithValue('Failed to fetch generic files.');
    }
  }
);

export const fetchResourceFiles = createAsyncThunk(
  'data/fetchResourceFiles',
  async ({ resourceId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/data/file-uploads/?resource=${resourceId}`);
      const data = response.data;
      return data.results;
    } catch (error) {
      return rejectWithValue('Failed to fetch resource files.');
    }
  }
);

// Thunk to fetch raw data
export const fetchRawData = createAsyncThunk(
  'data/fetchRawData',
  async ({startDate, endDate, resourceId, sessionOnly = false}, {rejectWithValue}) => {
    try {
      const hasStartDateTime = typeof startDate === 'string' && startDate.includes('T');
      const hasEndDateTime = typeof endDate === 'string' && endDate.includes('T');

      const mStart = moment.utc(startDate);
      const mEnd = moment.utc(endDate);
      const startDateOnly = mStart.format('YYYY-MM-DD');
      const endDateOnly = mEnd.format('YYYY-MM-DD');
      const finalStart = hasStartDateTime
        ? moment.utc(startDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
        : `${startDateOnly}T00:00:00Z`;
      const finalEnd = hasEndDateTime
        ? moment.utc(endDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
        : `${endDateOnly}T23:45:00Z`;

      let allResults = [];
      const initialParams = new URLSearchParams({
        start_date: finalStart,
        end_date: finalEnd,
        resource: resourceId,
      });
      let query = `?${initialParams.toString()}`;

      while (query) {
        // 3) If query starts with 'http', use it as-is; else prepend '/data/market-forecasts/'
        const finalUrl = query.startsWith('http')
          ? query
          : `/data/raw-measurements${query}`;

        const response = await axiosInstance.get(finalUrl);
        const data = response.data.data; // { count, next, previous, results }
        if (data.results.length === 0) {
          return rejectWithValue('No observed data available for the selected period.');
        }
        // 4) Accumulate
        if (Array.isArray(data.results)) {
          allResults.push(...data.results);
        }

        // 5) Next page: can be full URL or relative
        query = data.next;  // might be "http://..." or "?offset=1500..."
      }

      // todo dont need to be so strict - show what real values we have. But keep logic for
      //  for future reference
      // if (sessionOnly) {
      //   const expectedPoints = moment.utc(finalEnd).diff(moment.utc(finalStart), 'minutes') / 15 + 1;
      //   console.log(expectedPoints);
      //   console.log(allResults.length);
      //   if (allResults.length < expectedPoints) {
      //     return rejectWithValue('Incomplete data for the requested interval.');
      //   }
      //   // const foundStart = allResults.some(item => item.datetime === finalStart);
      //   // const foundEnd = allResults.some(item => item.datetime === finalEnd);
      //   //
      //   // // If either timestamp is missing in the results, reject:
      //   // if (!foundStart || !foundEnd) {
      //   //   return rejectWithValue('No data covering the entire requested range.');
      //   // }
      // }

      return allResults; // Return the combined list
    } catch (error) {
      return rejectWithValue('Failed to fetch market forecasts.');
    }
  }
);


export const fetchUserForecastData = createAsyncThunk(
  'data/userForecastData',
  async ({startDate, endDate, resourceId, sessionOnly = false, user=false, variable=false, horizonFilter = 'latest'}, {rejectWithValue}) => {
    try {
      const mStart = moment.utc(startDate);
      const mEnd = moment.utc(endDate);
      const startDateOnly = mStart.format('YYYY-MM-DD');
      const endDateOnly = mEnd.format('YYYY-MM-DD');
      const finalStart = `${startDateOnly}T00:00:00Z`;
      const finalEnd = `${endDateOnly}T23:59:59Z`;

      let allResults = [];
      let query = `?start_date=${finalStart}&end_date=${finalEnd}&resource=${resourceId}`;
      if (user) {
        query += `&user=${user}`;
      }
      if (variable) {
        query += `&variable=${variable}`;
      }
      query += `&horizon=${encodeURIComponent(horizonFilter)}`;
      query = appendLatestSessionOnlyParam(query, horizonFilter);
      while (query) {
        // 3) If query starts with 'http', use it as-is; else prepend '/data/individual-forecasts/'
        const finalUrl = query.startsWith('http')
          ? query
          : `/data/individual-forecasts${query}`;

        const response = await axiosInstance.get(finalUrl);
        const data = response.data.data; // { count, next, previous, results }

        // 4) Accumulate - tag each result with the variable since API doesn't return it
        if (Array.isArray(data.results)) {
          const taggedResults = variable
            ? data.results.map(item => ({ ...item, variable }))
            : data.results;
          allResults.push(...taggedResults);
        }

        // 5) Next page: can be full URL or relative
        query = data.next;  // might be "http://..." or "?offset=1500..."
      }

      return allResults; // Return the combined list
    } catch
      (error) {
      return rejectWithValue('Failed to fetch market forecasts.');
    }
  }
);

export const fetchUserContinuousForecasts = createAsyncThunk(
  'data/userContinuousForecasts',
  async ({startDate, endDate, resourceId, variable = false}, {rejectWithValue}) => {
    try {
      const mStart = moment.utc(startDate);
      const mEnd = moment.utc(endDate);
      const startDateOnly = mStart.format('YYYY-MM-DD');
      const endDateOnly = mEnd.format('YYYY-MM-DD');
      const finalStart = `${startDateOnly}T00:00:00Z`;
      const finalEnd = `${endDateOnly}T23:59:59Z`;

      let allResults = [];
      // No `user` param: the endpoint forces self for non-superusers.
      let query = `?start_date=${finalStart}&end_date=${finalEnd}&resource=${resourceId}`;
      if (variable) {
        query += `&variable=${variable}`;
      }
      while (query) {
        const finalUrl = query.startsWith('http')
          ? query
          : `/data/continuous-forecasts${query}`;

        const response = await axiosInstance.get(finalUrl);
        const data = response.data.data; // { count, next, previous, results }

        // Serializer already returns `variable`, so no client-side tagging.
        if (Array.isArray(data.results)) {
          allResults.push(...data.results);
        }

        query = data.next; // may be a full URL or a relative "?offset=..."
      }

      return allResults;
    } catch (error) {
      return rejectWithValue('Failed to fetch continuous forecasts.');
    }
  }
);

// Thunk to fetch forecasts for multiple users in parallel. With `append: true`
// in the argument the users are merged into those already loaded instead of
// replacing them (read by the reducers from meta.arg).
export const fetchMultipleUserForecasts = createAsyncThunk(
  'data/fetchMultipleUserForecasts',
  async ({ userIds, startDate, endDate, resourceId, horizonFilter = 'latest' }, { rejectWithValue, dispatch }) => {
    try {
      // Fetch forecasts for all users in parallel (only q50)
      const fetchPromises = userIds.map(async (userId) => {
        try {
          const result = await dispatch(
            fetchUserForecastData({
              startDate,
              endDate,
              resourceId,
              user: userId,
              variable: 'q50',
              horizonFilter,
            })
          ).unwrap();

          // The `user` field is the submitter's identifier from the API:
          // the username pseudonym for non-superusers, the email for
          // superusers (see audit A260606-API-H1). Used only as a chart label.
          const userName = result.length > 0 ? result[0].user : `User ${userId.slice(0, 8)}`;

          return {
            userId,
            userName,
            data: result,
          };
        } catch (error) {
          return {
            userId,
            userName: `User ${userId.slice(0, 8)}`,
            data: [],
            error: true,
          };
        }
      });

      const results = await Promise.all(fetchPromises);

      // Convert array to object keyed by userId
      const forecastsMap = {};
      results.forEach(({ userId, userName, data, error }) => {
        forecastsMap[userId] = { userName, data, error };
      });

      return forecastsMap;
    } catch (error) {
      return rejectWithValue('Failed to fetch multiple user forecasts.');
    }
  }
);

// New thunk: request individual forecast export
export const requestIndividualForecastExport = createAsyncThunk(
  'data/requestIndividualForecastExport',
  async (payload, {rejectWithValue}) => {
    // payload expected: { resource, start_date, end_date, metrics, user }
    try {
      // Use GET with query params - backend requires GET method
      // Filter out null/undefined values to avoid sending empty params
      const cleanParams = Object.entries(payload).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await axiosInstance.get('/data/individual-forecasts/export', {
        params: cleanParams
      });
      // Expecting 202/201/200 and possibly a JSON body with id/status/file_url
      return response.data || {};
    } catch (error) {
      // Extract error message from response.data.data.error structure
      const responseData = error?.response?.data || {};
      const errorData = responseData?.data || responseData;
      const msg = errorData?.error || responseData?.error || errorData?.detail || errorData?.message || error?.message || 'Failed to request export';
      return rejectWithValue({message: msg, ...errorData});
    }
  }
);

// New thunk: request raw measurement data export (CSV)
export const requestRawMeasurementsExport = createAsyncThunk(
  'data/requestRawMeasurementsExport',
  async (payload, {rejectWithValue}) => {
    // payload expected: { resource, start_date, end_date }
    try {
      const cleanParams = Object.entries(payload).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await axiosInstance.get('/data/raw-measurements/export', {
        params: cleanParams
      });
      return response.data || {};
    } catch (error) {
      const responseData = error?.response?.data || {};
      const errorData = responseData?.data || responseData;
      const msg = errorData?.error || responseData?.error || errorData?.detail || errorData?.message || error?.message || 'Failed to request export';
      return rejectWithValue({message: msg, ...errorData});
    }
  }
);

// New thunk: request market report export (xlsx scores)
export const requestMarketReportExport = createAsyncThunk(
  'data/requestMarketReportExport',
  async (payload, {rejectWithValue}) => {
    // payload expected: { resource, start_date, end_date, user }
    try {
      // Use GET with query params
      // Filter out null/undefined values to avoid sending empty params
      const cleanParams = Object.entries(payload).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await axiosInstance.get('/market/report/xlsx-scores', {
        params: cleanParams
      });
      // Expecting 202/201/200 and possibly a JSON body with id/status/file_url
      return response.data || {};
    } catch (error) {
      // Extract error message from response.data.data.error structure
      const responseData = error?.response?.data || {};
      const errorData = responseData?.data || responseData;
      const msg = errorData?.error || responseData?.error || errorData?.detail || errorData?.message || error?.message || 'Failed to request export';
      return rejectWithValue({message: msg, ...errorData});
    }
  }
);

// New thunk: request user activity export
export const requestUserActivityExport = createAsyncThunk(
  'data/requestUserActivityExport',
  async (payload, {rejectWithValue}) => {
    // payload expected: { user_id, activity_type, start_date, end_date }
    try {
      // Use GET with query params
      // Filter out null/undefined values to avoid sending empty params
      const cleanParams = Object.entries(payload).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== 'all') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await axiosInstance.get('/user/activity/export', {
        params: cleanParams
      });
      // Expecting 202 and JSON body with task_id/data_export_id/status
      return response.data || {};
    } catch (error) {
      // Extract error message from response.data.data.error structure
      const responseData = error?.response?.data || {};
      const errorData = responseData?.data || responseData;
      const msg = errorData?.error || responseData?.error || errorData?.detail || errorData?.message || error?.message || 'Failed to request export';
      return rejectWithValue({message: msg, ...errorData});
    }
  }
);

// New thunk: fetch exported forecast files / jobs
export const fetchExports = createAsyncThunk(
  'data/fetchExports',
  async ({ resourceId } = {}, { rejectWithValue }) => {
    try {
      // optional resource filter
      const params = {};
      if (resourceId) params.resource = resourceId;
      const response = await axiosInstance.get('/data/exports/', { params });
      const data = response.data;

      // Handle common payload shapes robustly:
      // - Array directly
      // - { results: [...] }
      // - { data: { results: [...] } }
      // - { data: [...] }
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) return data.results;
        if (data.data) {
          if (Array.isArray(data.data)) return data.data;
          if (data.data.results) return data.data.results;
        }
      }

      // fallback: return an empty array
      return [];
    } catch (error) {

      return rejectWithValue('Failed to fetch exports');
    }
  }
);

const dataSlice = createSlice({
  name: 'data',
  initialState: {
    selectedForecast: [{ id: null, name: 'Ensemble' }],
    rawData: null,
    sessionRawData: null,
    userForecastData: [],
    continuousForecastData: [],
    individualUserForecasts: {},
    // `append` requests of fetchMultipleUserForecasts in flight. Updated with
    // `loading` in the same action, so a view can tell an addition apart
    // from a full load in any render.
    individualAppendsInFlight: 0,
    historicalData: null,
    genericFiles: [],
    resourceFiles: [],
    loading: false,
    errorMessage: '',

    lastAddedResource: null,
    lastUpdatedResource: null,

    // track server-side export jobs/pending files
    pendingExports: [],
    exports: [],
  },
  reducers: {
    clearDataError(state) {
      state.errorMessage = '';
    },
    setHistoricalData(state, action) {
      state.historicalData = action.payload;
    },
    setSelectedForecast: (state, action) => {
      state.selectedForecast = action.payload;
    },
    clearHistoricalData(state) {
      state.historicalData = null;
    },
    clearUserForecastData(state) {
      state.userForecastData = null
    },
    clearContinuousForecastData(state) {
      state.continuousForecastData = null;
    },
    clearIndividualUserForecasts(state) {
      state.individualUserForecasts = {};
    },
    // Drops the given users (forecasters unticked on the Dashboard chart).
    removeIndividualUserForecasts(state, action) {
      action.payload.forEach((userId) => {
        delete state.individualUserForecasts[userId];
      });
    },
    clearRawData(state) {
      state.rawData = null;
    },
    clearSessionRawData(state) {
      state.sessionRawData = null;
    }
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchRawData.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchRawData.fulfilled, (state, action) => {
        const {sessionOnly} = action.meta.arg; // Extract the sessionOnly flag from meta
        state.loading = false;
        if (sessionOnly) {
          state.sessionRawData = action.payload; // Save to sessionRawData if sessionOnly is true
        } else {
          state.rawData = action.payload; // Save to rawData otherwise
        }
      })
      // Handle rejected state
      .addCase(fetchRawData.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      .addCase(fetchUserForecastData.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchUserForecastData.fulfilled, (state, action) => {
        state.loading = false;
        // Append results to support multiple quantile fetches
        if (!state.userForecastData) {
          state.userForecastData = [];
        }
        state.userForecastData.push(...action.payload);
      })
      .addCase(fetchUserForecastData.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      .addCase(fetchUserContinuousForecasts.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchUserContinuousForecasts.fulfilled, (state, action) => {
        state.loading = false;
        // Append to support the three per-quantile fetches.
        if (!state.continuousForecastData) {
          state.continuousForecastData = [];
        }
        state.continuousForecastData.push(...action.payload);
      })
      .addCase(fetchUserContinuousForecasts.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      .addCase(fetchMultipleUserForecasts.pending, (state, action) => {
        state.loading = true;
        state.errorMessage = '';
        // Each requested user gets an empty placeholder owned by this request,
        // and only the owner fills it: a response for a user removed or
        // re-requested meanwhile (or replaced by a newer full fetch) is dropped.
        const { userIds, append = false } = action.meta.arg;
        if (append) state.individualAppendsInFlight += 1;
        else state.individualUserForecasts = {};
        userIds.forEach((userId) => {
          state.individualUserForecasts[userId] = { data: [], pendingRequestId: action.meta.requestId };
        });
      })
      .addCase(fetchMultipleUserForecasts.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg.append) state.individualAppendsInFlight -= 1;
        Object.entries(action.payload).forEach(([userId, forecast]) => {
          if (state.individualUserForecasts[userId]?.pendingRequestId === action.meta.requestId) {
            state.individualUserForecasts[userId] = forecast;
          }
        });
      })
      .addCase(fetchMultipleUserForecasts.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
        if (action.meta.arg.append) state.individualAppendsInFlight -= 1;
        // Drop this request's placeholders so a later change retries them.
        action.meta.arg.userIds.forEach((userId) => {
          if (state.individualUserForecasts[userId]?.pendingRequestId === action.meta.requestId) {
            delete state.individualUserForecasts[userId];
          }
        });
      })
      .addCase(fetchGenericFiles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGenericFiles.fulfilled, (state, action) => {
        state.genericFiles = action.payload;
        state.loading = false;
      })
      .addCase(fetchGenericFiles.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })

      .addCase(fetchResourceFiles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchResourceFiles.fulfilled, (state, action) => {
        state.resourceFiles = action.payload;
        state.loading = false;
      })
      .addCase(fetchResourceFiles.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })
      .addCase(uploadFile.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload;
      })

      // Handle the new export request lifecycle
      .addCase(requestIndividualForecastExport.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(requestIndividualForecastExport.fulfilled, (state, action) => {
        state.loading = false;
        // server may return job information; keep it in the pendingExports list
        const payload = action.payload || {};
        state.pendingExports = [payload, ...state.pendingExports.filter(pe => pe?.server_id !== payload?.id && pe?.id !== payload?.id)];
      })
      .addCase(requestIndividualForecastExport.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Failed to request export';
      })

      // Handle the market report export request lifecycle
      .addCase(requestMarketReportExport.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(requestMarketReportExport.fulfilled, (state, action) => {
        state.loading = false;
        // server may return job information; keep it in the pendingExports list
        const payload = action.payload || {};
        state.pendingExports = [payload, ...state.pendingExports.filter(pe => pe?.server_id !== payload?.id && pe?.id !== payload?.id)];
      })
      .addCase(requestMarketReportExport.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Failed to request market report export';
      })

      .addCase(fetchExports.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchExports.fulfilled, (state, action) => {
        state.loading = false;
        state.exports = action.payload || [];
      })
      .addCase(fetchExports.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Failed to fetch exports';
      });
  },
});

// Action exports
export const {
  clearDataError,
  setHistoricalData,
  clearHistoricalData,
  clearRawData,
  clearSessionRawData,
  clearUserForecastData,
  clearContinuousForecastData,
  clearIndividualUserForecasts,
  removeIndividualUserForecasts,
  setSelectedForecast
} =
  dataSlice.actions;

// Selectors

export const selectRawData = (state) => state.data.rawData;
export const selectSessionRawData = (state) => state.data.sessionRawData;
export const selectHistoricalData = (state) => state.data.historicalData;
export const selectDataLoading = (state) => state.data.loading;
export const selectDataErrorMessage = (state) => state.data.errorMessage;
export const selectLastAddedResource = (state) => state.data.lastAddedResource;
export const selectLastUpdatedResource = (state) => state.data.lastUpdatedResource;
export const selectUserForecastData = (state) => state.data.userForecastData;
export const selectContinuousForecastData = (state) => state.data.continuousForecastData;
export const selectIndividualUserForecasts = (state) => state.data.individualUserForecasts;
export const selectIndividualAppendsInFlight = (state) => state.data.individualAppendsInFlight;
export const selectGenericFiles = (state) => state.data.genericFiles;
export const selectResourceFiles = (state) => state.data.resourceFiles;
export const selectLoading = (state) => state.data.loading;
export const selectPendingExports = (state) => state.data.pendingExports;
export const selectExports = (state) => state.data.exports;

export default dataSlice.reducer;
